<script>
import Layout from "../../layouts/main.vue"; 
import PageHeader from "@/components/page-header"; 
import appConfig from "../../../app.config"; 
import axios from "axios"; 
import moment from "moment"; 
export default 
{ 
    page: { 
        title: "Order Bulk Import", 
        meta: [ 
            { name: "description", content: appConfig.description, }, 
        ], 
    }, 
    
    components: { 
        Layout, 
        PageHeader, 
    }, 
    
    data() { 
        return { 
            title: "Order Bulk Import", 
            items: [ 
                { text: "Orders", href: "/", }, 
                { text: "Bulk Import", active: true, }, 
            ], 
            file: null, 
            isImporting: false, 
            importResult: null, 
            errorMessage: null, 
            cloudFrontURL: process.env.VUE_APP_BUCKET,
        }; 
    }, 
        
    computed: { 
        hasResult() { 
            return this.importResult !== null; 
        }, 
        
        hasUnmatchedOrders() { 
            return ( this.importResult && this.importResult.unmatched_orders && this.importResult.unmatched_orders.length > 0 ); 
        }, 
    }, 
    
    methods: { 
        onFileChange(event) { 
            this.errorMessage = null; 
            const files = event.target.files; 
            
            if (!files || files.length === 0) { 
                this.file = null; return; 
            } 
            
            this.file = files[0]; 
        }, 
        
        clearFile() { 
            this.file = null;
            if (this.$refs.fileInput) { 
                this.$refs.fileInput.value = ""; 
            } 
        }, 
        
        resetImport() { 
            this.clearFile(); 
            this.importResult = null; 
            this.errorMessage = null; }, 
            formatDate(value) { 
                if (!value) { return "---"; } 
                
                return moment(String(value)).format("YYYY-MM-DD HH:mm:ss"); 
            }, 
            
            async importOrders() { 
                if (!this.file) { 
                    this.errorMessage = "Please select a CSV or Excel file."; 
                    return; 
                } 
                
                this.isImporting = true; 
                this.errorMessage = null; 
                this.importResult = null; 
                const formData = new FormData(); 
                formData.append("file", this.file); 
                axios.defaults.headers.common["authorization"] = "Bearer " + localStorage.getItem("jwt"); 
                try { 
                    const response = await axios.post( process.env.VUE_APP_API_BASEURL + "order-fulfillment/import", 
                        formData, 
                        { 
                            headers: { "Content-Type": "multipart/form-data", }, 
                        } 
                    ); 
                
                    if (response && response.data) { 
                        this.importResult = response.data; // Clear selected file after successful import. 
                        this.clearFile();
                    } 
                } catch (error) { 
                    console.error("Order bulk import failed:", error); 
                    if (error.response && error.response.data) { 
                        this.errorMessage = error.response.data.message || "Unable to process the import."; 
                    } else { 
                        this.errorMessage = "Unable to process the import. Please try again."; 
                    }
                } finally { 
                    this.isImporting = false; 
                } 
            }, 
        }, 
    };
</script>
<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <!-- Upload -->
    <div class="card">
      <div class="card-header border-bottom-dashed">
        <h5 class="card-title mb-1">
          <i class="ri-upload-cloud-2-line me-2 text-primary"></i> 
          Order Fulfillment Bulk Import
        </h5>
        <p class="text-muted mb-0 font-size-12">
          Upload a CSV or Excel file to import order fulfilment information.
        </p>
      </div>
      <div class="card-body">
        <!-- Error -->
        <div
          v-if="errorMessage"
          class="alert alert-danger d-flex align-items-start"
          role="alert"
        >
          <i class="ri-error-warning-line me-2 mt-1"></i>
          <div>
            <strong>Import failed</strong>
            <div class="mt-1">{{ errorMessage }}</div>
          </div>
        </div>
        <!-- File -->
        <div class="mb-3">
          <label class="form-label fw-medium">
            Import File <span class="text-danger">*</span>
          </label>
          <input
            ref="fileInput"
            type="file"
            class="form-control"
            accept=".csv,.txt,.xlsx,.xls"
            @change="onFileChange"
          />
          <div class="form-text">
            <a v-bind:href="`${cloudFrontURL}fulfillments/template.xlsx`" target="_blank" rel="noreferrer noopener" style="margin-right: 10px;">Download Template</a>
            Supported formats: CSV, XLS, XLSX.
          </div>
        </div>
        <!-- Selected file -->
        <div
          v-if="file"
          class="alert alert-light border d-flex align-items-center justify-content-between"
        >
          <div>
            <i class="ri-file-excel-2-line text-success me-2"></i>
            <strong>{{ file.name }}</strong>
            <small class="text-muted ms-2">
              {{ (file.size / 1024 / 1024).toFixed(2) }} MB
            </small>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-light"
            @click="clearFile"
            :disabled="isImporting"
          >
            <i class="ri-close-line"></i>
          </button>
        </div>
        <!-- Expected columns -->
        <div class="alert alert-info mb-0">
          <div class="fw-semibold mb-1">Expected columns</div>
          <code> Order No, Imeis or Serial No, Tracking No, Reverse to Pending ('yes') </code>
        </div>
        <!-- Actions -->
        <div class="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            class="btn btn-light"
            @click="resetImport"
            :disabled="isImporting"
          >
            <i class="ri-refresh-line me-1"></i> Reset
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="importOrders"
            :disabled="isImporting || !file"
          >
            <template v-if="isImporting">
              <span
                class="spinner-border spinner-border-sm me-1"
                role="status"
              ></span>
              Importing...
            </template>
            <template v-else>
              <i class="ri-upload-2-line me-1"></i> Import Orders
            </template>
          </button>
        </div>
      </div>
    </div>
    <!-- Import Result -->
    <div v-if="hasResult" class="card">
      <div class="card-header border-bottom-dashed">
        <h5 class="card-title mb-1">
          <i class="ri-checkbox-circle-line text-success me-2"></i> Import
          Completed
        </h5>
        <p class="text-muted mb-0 font-size-12">
          The import has finished processing.
        </p>
      </div>
      <div class="card-body">
        <!-- Summary -->
        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <div class="border rounded p-3 h-100">
              <div class="text-muted font-size-12">Total Records</div>
              <div class="fs-4 fw-semibold mt-1">
                {{ importResult.summary.total }}
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="border rounded p-3 h-100">
              <div class="text-muted font-size-12">Successfully Matched</div>
              <div class="fs-4 fw-semibold text-success mt-1">
                {{ importResult.summary.matched }}
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="border rounded p-3 h-100">
              <div class="text-muted font-size-12">Unmatched</div>
              <div class="fs-4 fw-semibold text-danger mt-1">
                {{ importResult.summary.unmatched }}
              </div>
            </div>
          </div>
        </div>
        <!-- Success message -->
        <div
          v-if="importResult.summary.matched > 0"
          class="alert alert-success d-flex align-items-start"
        >
          <i class="ri-checkbox-circle-line me-2 mt-1"></i>
          <div>
            <strong>
              {{ importResult.summary.matched }} order(s) processed
              successfully.
            </strong>
            <div class="mt-1 font-size-12">
              Only orders matching existing records were processed.
            </div>
          </div>
        </div>
        <!-- No issues -->
        <div v-if="!hasUnmatchedOrders" class="alert alert-success mb-0">
          <i class="ri-check-double-line me-1"></i> All imported orders were
          matched successfully.
        </div>
        <!-- Unmatched -->
        <div v-if="hasUnmatchedOrders">
          <div class="alert alert-warning d-flex align-items-start">
            <i class="ri-alert-line me-2 mt-1"></i>
            <div>
              <strong>
                {{ importResult.summary.unmatched }} order(s) could not be
                matched.
              </strong>
              <div class="mt-1 font-size-12">
                These records were skipped and were not processed.
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center mb-3">
            <h6 class="mb-0">Unmatched Orders</h6>
            <span class="badge bg-danger ms-2">
              {{ importResult.summary.unmatched }}
            </span>
          </div>
          <div class="table-responsive">
            <table class="table table-bordered table-hover mb-0">
              <thead class="table-secondary">
                <tr>
                  <th>#</th>
                  <th>Order ID</th>
                  <th>Serial No</th>
                  <th>IMEI 1</th>
                  <th>IMEI 2</th>
                  <th>Tracking Number</th>
                  <th>Issue</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in importResult.unmatched_orders"
                  :key="index"
                >
                  <td>{{ index + 1 }}</td>
                  <td class="fw-semibold">{{ item.order_id || "---" }}</td>
                  <td>{{ item.serial_no || "---" }}</td>
                  <td class="font-monospace font-size-12">
                    {{ item.imei_1 || "---" }}
                  </td>
                  <td class="font-monospace font-size-12">
                    {{ item.imei_2 || "---" }}
                  </td>
                  <td>{{ item.tracking_no || "---" }}</td>
                  <td>
                    <span class="badge bg-soft-danger text-danger">
                      {{ item.reason }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
<style scoped>
.font-monospace {
  font-family: monospace;
}
.card {
  margin-bottom: 1rem;
}
</style>
